import defaultReleaseNotes from '../../RELEASE_NOTES.md?raw';

export interface ReleaseChange {
  category: string;
  text: string;
  commitHash?: string;
  commitUrl?: string;
}

export interface ReleaseNote {
  version: string;
  title: string;
  summaryTitle?: string;
  date: string;
  timestamp: string;
  url?: string;
  changes: ReleaseChange[];
  rawContent: string;
}

const SEMVER_REGEX = /\b\d+\.\d+\.\d+(?:-[\w.-]+)?\b/;
const URL_REGEX = /\]\((https?:\/\/[^\s)]+)\)/;
const DATE_REGEX = /\b(\d{4}-\d{2}-\d{2})\b/;
const COMMIT_LINK_REGEX = /\(\[(\w{7,40})\]\(([^)]+)\)\)$/;

export function parseReleaseNotes(sourceText: string = defaultReleaseNotes): ReleaseNote[] {
  if (!sourceText || typeof sourceText !== 'string') {
    return [];
  }

  const lines = sourceText.split(/\r?\n/);
  const releases: ReleaseNote[] = [];
  let currentRelease: Partial<ReleaseNote> | null = null;
  let currentCategory = 'General';
  let contentLines: string[] = [];

  const finalizeCurrentRelease = () => {
    if (!currentRelease?.version) return;

    let isoTimestamp = new Date().toISOString();
    if (currentRelease.date) {
      const parsedDate = new Date(`${currentRelease.date}T00:00:00.000Z`);
      if (!isNaN(parsedDate.getTime())) {
        isoTimestamp = parsedDate.toISOString();
      }
    }

    const title = currentRelease.summaryTitle
      ? `v${currentRelease.version}: ${currentRelease.summaryTitle}`
      : `v${currentRelease.version}`;

    releases.push({
      version: currentRelease.version,
      title,
      summaryTitle: currentRelease.summaryTitle,
      date: currentRelease.date || '',
      timestamp: isoTimestamp,
      url: currentRelease.url,
      changes: currentRelease.changes || [],
      rawContent: contentLines.join('\n').trim()
    });
  };

  for (const line of lines) {
    const trimmed = line.trim();
    const isMainHeading = trimmed.startsWith('# ') || trimmed.startsWith('## ') || trimmed.startsWith('### ');

    if (isMainHeading) {
      const verMatch = trimmed.match(SEMVER_REGEX);
      if (verMatch) {
        finalizeCurrentRelease();

        const version = verMatch[0];
        const urlMatch = trimmed.match(URL_REGEX);
        const dateMatch = trimmed.match(DATE_REGEX);

        currentRelease = {
          version,
          date: dateMatch ? dateMatch[1] : '',
          url: urlMatch ? urlMatch[1] : undefined,
          changes: []
        };
        currentCategory = 'Updates';
        contentLines = [];
        continue;
      }
    }

    if (!currentRelease) {
      continue;
    }

    contentLines.push(line);

    if (trimmed.startsWith('#### ') || (trimmed.startsWith('### ') && !trimmed.match(SEMVER_REGEX))) {
      const headingText = trimmed.replace(/^#+\s+/, '').trim();
      currentCategory = headingText;
      if (!currentRelease.summaryTitle) {
        currentRelease.summaryTitle = headingText;
      }
      continue;
    }

    if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
      let rawText = trimmed.substring(2).trim();
      let commitHash: string | undefined;
      let commitUrl: string | undefined;

      const commitMatch = rawText.match(COMMIT_LINK_REGEX);
      if (commitMatch) {
        commitHash = commitMatch[1];
        commitUrl = commitMatch[2];
        rawText = rawText.replace(COMMIT_LINK_REGEX, '').trim();
      }

      currentRelease.changes = currentRelease.changes || [];
      currentRelease.changes.push({
        category: currentCategory,
        text: rawText,
        commitHash,
        commitUrl
      });
    }
  }

  finalizeCurrentRelease();

  return releases;
}

export function getLatestRelease(sourceText?: string): ReleaseNote | null {
  const notes = parseReleaseNotes(sourceText);
  return notes.length > 0 ? notes[0] : null;
}
